import { termsConfig } from "@/types/adaptation";
import { formatAbbreviatedValueWithMetricUnit } from "@/utils/number";
import { EyeOutlined } from "@ant-design/icons";
import { CaretRightIcon, MinusCircleIcon, TrophyIcon } from "@phosphor-icons/react/dist/ssr";
import { useVirtualizer, VirtualItem } from "@tanstack/react-virtual";
import { Button, Tooltip } from "antd";
import classNames from "classnames";
import React, { PropsWithChildren, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import AdaptationDumbbellTooltip from "./AdaptationDumbellTooltip";
import { computeLabelColumnWidth, percentInDomain, projectDumbbell, ticksForDomain } from "./common/dumbbellChartGeometry";
import { MvarDerivedUnavailableNotice } from "./common/MvarDerivedUnavailableNotice";
import styles from "./DumbellChart.module.scss";
const TooltipOverlay = ({
  containerId,
  children
}: PropsWithChildren & {
  containerId?: string;
}) => {
  const [container, setContainer] = useState<HTMLElement | null>(null);
  useEffect(() => {
    setContainer(containerId ? document.getElementById(containerId) : null);
  }, [containerId]);
  if (container === undefined || container === null || !container) return <>{children}</>;
  return createPortal(<>{children}</>, container);
};
const ROW_HEIGHT = 38;
const OVERSCAN = 5; // num of extra items to render below/above visible area
const PLOT_INSET = 10;
const AXIS_HEIGHT = 30; // matches the h-[30px] sticky axis header
const ROW_PADDING = 4;
const HIGHLIGHT_BTN_WIDTH = 30;
const EXCLUDE_BTN_WIDTH = 30;
const CTA_WIDTH = 56;
const CARET_ARM = 10;
const CARET_ARM_HOVERED = 12;
const CARET_WIDTH_PX = CARET_ARM_HOVERED * Math.SQRT2; // width when caret arm rotated 45°

const TROPHY_GUTTER = 20;
const TROPHY_TINT = `${termsConfig.absoluteChange.color}0d`;
const TROPHY_TINT_HOVER = `${termsConfig.absoluteChange.color}14`;
type DumbellData = {
  key: string;
  name: string;
  start: number;
  end: number;
  isTopDecile?: boolean;
  /** Kept out of the axis domain. The row still lists, with "Excluded" in place of its marks. */
  isExcluded?: boolean;
};
const hasOwnTooltip = (e: React.MouseEvent) => e.target instanceof Element && e.target.closest("[data-own-tooltip]") !== null;
export const DumbbellChart = ({
  data,
  domain = [0, 100],
  startLabel,
  endLabel,
  startColor = "",
  endColor = "",
  onSelect,
  onHighlight,
  onToggleExclude,
  excludeDisabledReason,
  highlightedKey,
  dataUnit,
  showCustomAdaptationTooltip = false,
  tooltipFooter,
  tooltipContainerId,
  xAxisLabel,
  tooltipValueLabel,
  scrollElement,
  headerHeight: precedingHeight,
  labelColumnWidth,
  scrollResetKey,
  riskColorSections = [],
  hasTrophies = false,
  firstRowRef,
  dataAvailable = true,
  unavailableMessage
}: {
  data: DumbellData[];
  domain: [number, number];
  startLabel: string;
  endLabel: string;
  startColor: string;
  endColor: string;
  onSelect: (pointId: string) => void;
  onHighlight: (pointId: string) => void;
  onToggleExclude?: (pointId: string) => void;
  excludeDisabledReason?: string;
  highlightedKey?: string | null;
  dataUnit: "%" | "$";
  showCustomAdaptationTooltip?: boolean;
  tooltipFooter?: React.ReactNode;
  tooltipContainerId?: string;
  xAxisLabel: string;
  tooltipValueLabel: string;
  scrollElement: HTMLElement | null;
  headerHeight: number;
  labelColumnWidth?: number;
  scrollResetKey?: string;
  riskColorSections?: {
    from: number;
    to: number;
    color: string;
  }[];
  /** Whether the (unfiltered) dataset awards any trophies — reserves the gutter. Must NOT be derived
   * from `data`, which is search-filtered: the gutter would collapse mid-typing and shift the plot. */
  hasTrophies?: boolean;
  /** Binds the topmost rendered row, so a tour can highlight a single dumbbell. */
  firstRowRef?: React.Ref<HTMLDivElement>;
  /** False when the current selection has no data to plot: the marks and axis are left blank. */
  dataAvailable?: boolean;
  /** Explains the blank plot; shown only when `dataAvailable` is false. */
  unavailableMessage?: string;
}) => {
  const {
    t
  } = useTranslation("terms");
  const [hoveredName, setHoveredName] = useState<string | null>(null);
  const [tooltip, setTooltip] = useState<{
    row: DumbellData;
    x: number;
    y: number;
  } | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const plotRef = useRef<HTMLDivElement>(null);
  const [plotWidth, setPlotWidth] = useState(0);
  const scrollMargin = precedingHeight + AXIS_HEIGHT;
  const rowVirtualizer = useVirtualizer({
    count: data.length,
    getScrollElement: () => scrollElement,
    estimateSize: () => ROW_HEIGHT,
    overscan: OVERSCAN,
    scrollMargin
  });

  // reset scroll to top when the reader's frame of reference changes (sort / search / metric)
  useEffect(() => {
    rowVirtualizer.scrollToOffset(0);
  }, [scrollResetKey, rowVirtualizer]);
  const measuredLabelWidth = useMemo(() => computeLabelColumnWidth(data.map(item => item.name)), [data]);
  const yAxisWidth = labelColumnWidth ?? measuredLabelWidth;
  const trophyGutter = hasTrophies ? TROPHY_GUTTER : 0;
  const excludeGutter = onToggleExclude ? EXCLUDE_BTN_WIDTH : 0;
  const plotLeftOffset = yAxisWidth + HIGHLIGHT_BTN_WIDTH + excludeGutter + trophyGutter;
  useLayoutEffect(() => {
    const el = plotRef.current;
    if (!el) return;
    const update = () => setPlotWidth(el.clientWidth);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // percent is the only unit the geometry has; until the plot is measured its own fallback stands in
  const minCaretSpanPercent = plotWidth > 0 ? CARET_WIDTH_PX / plotWidth * 100 : undefined;
  const xPercent = (v: number) => percentInDomain(v, domain);
  const ticks = useMemo(() => ticksForDomain(domain), [domain]);
  const totalHeight = rowVirtualizer.getTotalSize();
  const virtualRows = rowVirtualizer.getVirtualItems();

  // tooltip position with edge-flip near the viewport edges
  const toolTipMargin = 16;
  const tooltipOffsetWidth = tooltipRef.current?.offsetWidth ?? 0;
  const tooltopOffsetHeight = tooltipRef.current?.offsetHeight ?? 0;
  const tooltipLeft = tooltip ? tooltip.x + toolTipMargin + tooltipOffsetWidth > window.innerWidth ? Math.max(toolTipMargin / 2, tooltip.x - toolTipMargin - tooltipOffsetWidth) : tooltip.x + toolTipMargin : 0;
  const tooltipTop = tooltip ? tooltip.y + toolTipMargin + tooltopOffsetHeight > window.innerHeight ? Math.max(toolTipMargin / 2, tooltip.y - tooltopOffsetHeight) : tooltip.y + toolTipMargin : 0;
  return <div className="flex w-full flex-col" data-sentry-component="DumbbellChart" data-sentry-source-file="DumbellChart.tsx">
      <div className="sticky top-0 z-10 flex h-[30px] shrink-0 border-b border-gray-200 bg-surface px-1 pt-[8px]">
        <div className="shrink-0 text-right" style={{
        width: plotLeftOffset
      }}>
          {xAxisLabel && <div className="text-xs font-semibold text-gray-500">{xAxisLabel}</div>}
        </div>
        {/* ref to measure plot width. dumbell direction caret only drawn if line is wide enough */}
        <div ref={plotRef} className="relative flex-1 border-l border-gray-200" style={{
        margin: `0 ${PLOT_INSET}px`
      }}>
          {dataAvailable && ticks.map((t, i) => <span key={i} className="absolute whitespace-nowrap pl-2 text-xs" style={{
          left: `${xPercent(t)}%`,
          transform: i === 0 ? "translateX(0)" : i === ticks.length - 1 ? "translateX(-100%)" : "translateX(-50%)"
        }}>
                {formatAbbreviatedValueWithMetricUnit(t, dataUnit)}
              </span>)}
        </div>
        <div className="shrink-0" style={{
        width: CTA_WIDTH
      }} />
      </div>

      {/* The plot is blank rather than veiled, so every row stays clickable; this says why. */}
      {!dataAvailable && unavailableMessage && <MvarDerivedUnavailableNotice message={unavailableMessage} className="border-b border-red-100" />}

      <div>
        <div className="relative" style={{
        height: totalHeight
      }}>
          {/* risk-band background layer — behind gridlines */}
          {riskColorSections.length > 0 && dataAvailable && <div className="pointer-events-none absolute inset-y-0" style={{
          left: ROW_PADDING + plotLeftOffset + PLOT_INSET,
          right: ROW_PADDING + PLOT_INSET + CTA_WIDTH
        }}>
              {riskColorSections.map((s, i) => {
            const left = xPercent(s.from);
            const right = xPercent(s.to);
            return <div key={i} className="absolute inset-y-0" style={{
              left: `${left}%`,
              width: `${right - left}%`,
              background: s.color,
              opacity: 0.12
            }} />;
          })}
            </div>}

          {/* gridline layer */}
          {dataAvailable && <div className="pointer-events-none absolute inset-y-0" style={{
          left: ROW_PADDING + plotLeftOffset + PLOT_INSET,
          right: ROW_PADDING + PLOT_INSET + CTA_WIDTH
        }}>
              {ticks.map((t, i) => <div key={i} className="absolute inset-y-0 border-l border-dashed border-gray-200" style={{
            left: `${xPercent(t)}%`
          }} />)}
            </div>}

          {/* visible rows */}
          {virtualRows.map((virtualRow: VirtualItem) => {
          const row = data[virtualRow.index];
          const isHovered = row.name === hoveredName;
          const radius = isHovered ? 8 : 6;
          const diameter = radius * 2;
          const lineWidth = isHovered ? 4 : 3;
          const caretArm = isHovered ? CARET_ARM_HOVERED : CARET_ARM;
          const caretStroke = isHovered ? 2 : 1.5;
          const geometry = projectDumbbell(row.start, row.end, domain, {
            minCaretSpanPercent
          });
          return <div key={row.key} ref={virtualRow.index === 0 ? firstRowRef : undefined} role="button" tabIndex={0} onMouseEnter={e => {
            setHoveredName(row.name);
            setTooltip(hasOwnTooltip(e) ? null : {
              row,
              x: e.clientX,
              y: e.clientY
            });
          }} onMouseMove={e => setTooltip(hasOwnTooltip(e) ? null : {
            row,
            x: e.clientX,
            y: e.clientY
          })} onMouseLeave={() => {
            setHoveredName(null);
            setTooltip(null);
          }} onClick={() => onSelect(row.key)} onKeyDown={e => {
            if (e.target !== e.currentTarget) return;
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onSelect(row.key);
            }
          }} className={classNames("group absolute inset-x-0 top-0 flex cursor-pointer items-center px-1", {
            "bg-gray-950/5": isHovered && !row.isTopDecile,
            // muted unplotted rows, except on hover
            "opacity-50": (row.isExcluded || geometry.kind !== "visible") && !isHovered
          })} style={{
            height: ROW_HEIGHT,
            transform: `translateY(${virtualRow.start - scrollMargin}px)`,
            ...(row.isTopDecile ? {
              background: isHovered ? TROPHY_TINT_HOVER : TROPHY_TINT
            } : {})
          }}>
                <div className="flex shrink-0 justify-center" data-own-tooltip style={{
              width: HIGHLIGHT_BTN_WIDTH
            }}>
                  <Tooltip title="Highlight on map" placement="left" classNames={{
                root: "bg-surface",
                container: "bg-surface"
              }}>
                    <Button type={row.key === highlightedKey ? "primary" : "text"} size="small" icon={<EyeOutlined />} onClick={e => {
                  e.stopPropagation();
                  e.preventDefault();
                  onHighlight(row.key);
                }} aria-label={`Highlight ${row.name} on map`} className={classNames("shrink-0 text-gray-400", row.key === highlightedKey ? "text-white" : "")} />
                  </Tooltip>
                </div>

                {onToggleExclude && <Tooltip rootClassName="bg-surface" title={row.isExcluded ? "Include in chart" : excludeDisabledReason ?? "Exclude from chart"}>
                    <div className="flex shrink-0 justify-center" data-own-tooltip style={{
                width: EXCLUDE_BTN_WIDTH
              }}>
                      <Button type={row.isExcluded ? "primary" : "text"} size="small" disabled={!row.isExcluded && excludeDisabledReason !== undefined} icon={<MinusCircleIcon size={16} />} onClick={e => {
                  e.stopPropagation();
                  e.preventDefault();
                  onToggleExclude(row.key);
                }} aria-label={row.isExcluded ? `Show ${row.name} on the chart` : `Hide ${row.name} from the chart`} className={classNames("shrink-0", row.isExcluded ? "text-white" : "text-gray-400")} />
                    </div>
                  </Tooltip>}

                {hasTrophies && <div className="flex shrink-0 items-center justify-center" style={{
              width: TROPHY_GUTTER
            }}>
                    {row.isTopDecile && <Tooltip title={t("topDecileAbsoluteChange")}>
                        <span role="img" aria-label={t("topDecileAbsoluteChange")} data-own-tooltip className="flex items-center">
                          <TrophyIcon size={14} weight="fill" color={termsConfig.absoluteChange.color} />
                        </span>
                      </Tooltip>}
                  </div>}

                {/* label */}
                <div className={classNames("shrink-0 overflow-hidden truncate whitespace-nowrap pr-1 text-sm", styles.yLabel, {
              "font-semibold": isHovered
            })} title={row.name} style={{
              width: yAxisWidth
            }}>
                  {row.name}
                </div>

                {/* plot — drawn only when the metric has data for the selected hazard */}
                <div className="relative h-full flex-1" style={{
              margin: `0 ${PLOT_INSET}px`
            }}>
                  {dataAvailable && (row.isExcluded ? <span className="flex h-full items-center px-2 text-xs italic text-gray-400">
                        Excluded from chart
                      </span> : geometry.kind !== "visible" ?
              // dumbbell completely falls outside the window but the tooltip shows values.
              <span className="flex h-full items-center px-2 text-xs italic text-gray-400">
                        {geometry.kind === "off-left" ? "Below axis range" : "Above axis range"}
                      </span> : <>
                        <div className="absolute top-1/2 -translate-y-1/2 bg-slate-400" style={{
                  left: `${geometry.barLeft}%`,
                  width: `${geometry.barWidth}%`,
                  height: lineWidth
                }} />
                        {geometry.caretPercent !== null && <div aria-hidden className="absolute top-1/2 border-l-0 border-t-0 border-solid border-b-slate-400 border-r-slate-400" style={{
                  left: `${geometry.caretPercent}%`,
                  width: caretArm,
                  height: caretArm,
                  borderRightWidth: caretStroke,
                  borderBottomWidth: caretStroke,
                  transform: `translate(-50%, -50%) rotate(${geometry.caretPointsLeft ? 135 : -45}deg)`
                }} />}
                        {geometry.startPercent !== null && <div className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full" style={{
                  left: `${geometry.startPercent}%`,
                  width: diameter,
                  height: diameter,
                  background: startColor
                }} />}
                        {geometry.endPercent !== null && <div className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full" style={{
                  left: `${geometry.endPercent}%`,
                  width: diameter,
                  height: diameter,
                  background: endColor
                }} />}
                      </>)}
                </div>

                <Tooltip title="View">
                  <div className="flex shrink-0 items-center justify-end" data-own-tooltip style={{
                width: CTA_WIDTH
              }}>
                    <div className={`flex items-center rounded-full py-0.5 pl-2 pr-1 transition-colors duration-200 ${isHovered ? "bg-gray-200 !text-gray-500" : ""}`}>
                      <span aria-hidden className="max-w-0 translate-x-1 overflow-hidden whitespace-nowrap text-xs opacity-0 transition-all duration-200 group-hover:mr-1 group-hover:max-w-[40px] group-hover:translate-x-0 group-hover:opacity-100">
                        View
                      </span>
                      <CaretRightIcon size={12} weight="bold" className="shrink-0" />
                    </div>
                  </div>
                </Tooltip>
              </div>;
        })}
        </div>
      </div>

      {/* hover tooltip */}
      {tooltip && dataAvailable && <TooltipOverlay containerId={tooltipContainerId}>
          <div ref={tooltipRef} className="pointer-events-none fixed z-[1060]" style={{
        left: tooltipLeft,
        top: tooltipTop
      }}>
            {showCustomAdaptationTooltip ? <AdaptationDumbbellTooltip active payload={[{
          payload: tooltip.row
        }]} startLabel={startLabel} endLabel={endLabel} startColor={startColor} endColor={endColor} unit={dataUnit} footer={tooltipFooter} label={tooltipValueLabel} /> : <div className="rounded-lg border border-gray-200 bg-surface/80 p-3 text-gray-700 shadow-lg backdrop-blur">
                <div className="font-semibold">{tooltip.row.name}</div>
                <div>
                  {startLabel}: {formatAbbreviatedValueWithMetricUnit(tooltip.row.start, dataUnit)}
                </div>
                <div>
                  {endLabel}: {formatAbbreviatedValueWithMetricUnit(tooltip.row.end, dataUnit)}
                </div>
              </div>}
          </div>
        </TooltipOverlay>}
    </div>;
};